import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './LivePulse.sass';

// Fisher-Yates shuffle algorithm for randomizing array
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const LivePulse = ({ inline = false }) => {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const hasInitialized = useRef(false);

  // Fetch activity feed
  useEffect(() => {
    const fetchActivity = async (isInitial = false) => {
      try {
        const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const res = await fetch(`${base}/api/activity/feed`);
        const data = await res.json();
        if (data.ok && data.events && data.events.length > 0) {
          // Shuffle events randomly for each user on each page load
          // Server already shuffles, but we shuffle again client-side for extra randomness
          const shuffledEvents = shuffleArray(data.events);
          setEvents(shuffledEvents);
          
          // Randomize starting index only on initial page load
          if (isInitial && !hasInitialized.current) {
            const randomStartIndex = Math.floor(Math.random() * shuffledEvents.length);
            setCurrentIndex(randomStartIndex);
            hasInitialized.current = true;
          }
          
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } catch (err) {
        console.error('Failed to fetch activity feed:', err);
        setIsVisible(false);
      }
    };
    
    // Initial fetch with randomization
    fetchActivity(true);
    
    // Poll every 15 seconds (updates events but doesn't change starting position)
    const interval = setInterval(() => {
      fetchActivity(false);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Rotate through events every 5 seconds
  useEffect(() => {
    if (events.length === 0) return;
    
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % events.length);
    }, 5000);
    
    return () => clearInterval(timer);
  }, [events.length]);

  if (!isVisible || events.length === 0) return null;

  const currentEvent = events[currentIndex];

  return (
    <div className={`live-pulse${inline ? ' live-pulse-inline' : ''}`}>
      {/* Breathing pulse indicator */}
      <span className="live-pulse-dot" />
      
      <AnimatePresence mode="wait">
        <motion.span
          key={currentEvent.id || currentIndex}
          className="live-pulse-text"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {currentEvent.copy}
        </motion.span>
      </AnimatePresence>
    </div>
  );
};

export default LivePulse;

